import { IsStringEmpty } from './GlobalFunctions'

export type PathParameters = (string | number)[]

export type URLConstructorParams = {
    baseURL?: string
    pathParams?: PathParameters
    queryParams?: Record<string, Definable<string | number>>
}

// URL separator.
const SLASH: string = '/'

/**
 * URL builder for building valid encoded URLs.
 */
export class URLConstructor {
    private url: string
    private pathParams: PathParameters
    private queryParams: Record<string, Definable<string | number | string[] | boolean>>

    constructor({ baseURL, pathParams, queryParams }: URLConstructorParams) {
        this.url = baseURL ?? location.origin
        this.pathParams = pathParams ?? []
        this.queryParams = queryParams ?? {}
    }

    /**
     * Append path parameters.
     *
     * @param pathParams - Path parameters to append to the URL.
     */
    public addPathParams(...pathParams: PathParameters): URLConstructor {
        this.pathParams.push(...pathParams)

        return this
    }

    /**
     * Append query parameters.
     *
     * @param queryParams - Query parameters to append to the URL.
     */
    public addQueryParams(
        queryParams: Record<string, Definable<string | number | string[] | boolean>> = {}
    ): URLConstructor {
        this.queryParams = {
            ...this.queryParams,
            ...queryParams
        }

        return this
    }

    /**
     * Append path parameters.
     * Always use this version of the method if you are going to be returning the URL constructor from a method.
     *
     * @param pathParams - Path parameters to append to the URL.
     *
     * @return - Cloned version of the URL constructor.
     */
    public appendPathParams(...pathParams: PathParameters): URLConstructor {
        return this.clone().addPathParams(...pathParams)
    }

    /**
     * Append query parameters.
     * Always use this version of the method if you are going to be returning the URL constructor from a method.
     *
     * @param queryParams - Query parameters to append to the URL.
     *
     * @return - Cloned version of the URL constructor.
     */
    public appendQueryParams(
        queryParams: Record<string, Definable<string | number | string[] | boolean>> = {}
    ): URLConstructor {
        return this.clone().addQueryParams(queryParams)
    }

    /**
     * Construct the URL from the components listed.
     */
    public constructURL(): string {
        const searchParams: URLSearchParams = new URLSearchParams()
        Object.entries(this.queryParams).forEach(
            ([key, value]: [string, Definable<string | number | string[] | boolean>]): void => {
                if (value) {
                    if (Array.isArray(value)) {
                        value.forEach((val: string): void => searchParams.append(key, val))
                    } else {
                        searchParams.append(key, `${value}`)
                    }
                }
            }
        )
        const searchString: string = searchParams.toString()
        const url: URL = new URL(
            this.pathParams
                .map(String)
                .filter((pathParam: string): boolean => !IsStringEmpty(pathParam))
                .map((pathParam: string): string =>
                    pathParam.includes(SLASH) ? encodeURI(pathParam) : encodeURIComponent(pathParam)
                )
                .join(SLASH),
            this.url
        )

        const urlHasParams: boolean = url.searchParams.size > 0

        return `${url.toString()}${!IsStringEmpty(searchString) ? `${urlHasParams ? '&' : '?'}${searchString}` : ''}`
    }

    /**
     * Getter for the URL.
     */
    public getURL(): string {
        return this.url
    }

    /**
     * Setter for the URL.
     *
     * @param url - URL to set.
     */
    private setURL(url: string): URLConstructor {
        this.url = url
        return this
    }

    /**
     * Setter for the path parameters.
     *
     * @param pathParams - Path parameters to set.
     */
    private setPathParam(pathParams: PathParameters): URLConstructor {
        this.pathParams = pathParams
        return this
    }

    /**
     * Setter for the query parameters.
     *
     * @param queryParams - Query parameters to set.
     */
    private setQueryParams(
        queryParams: Record<string, Definable<string | number | string[] | boolean>>
    ): URLConstructor {
        this.queryParams = queryParams
        return this
    }

    // Create a deep copy of the URL constructor.
    private clone(): URLConstructor {
        return URLConstructor.newInstance()
            .setURL(`${this.url}`)
            .setPathParam([...this.pathParams])
            .setQueryParams({ ...this.queryParams })
    }

    /**
     * Override the toString method to construct the URL.
     *
     * @Override
     */
    public toString(): string {
        return this.constructURL()
    }

    /**
     * Create a new URL constructor from an existing constructor.
     *
     * @param existingConstructor - The existing URL constructor.
     */
    public static fromExisting(existingConstructor: URLConstructor): URLConstructor {
        return existingConstructor.clone()
    }

    // New instance of the URL constructor.
    public static newInstance(constructorParams?: URLConstructorParams): URLConstructor {
        return new URLConstructor(constructorParams ?? {})
    }

    // Websocket URL constructor.
    public static newWebsocketURL(constructorParams?: URLConstructorParams): URLConstructor {
        return URLConstructor.newInstance({
            baseURL: `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`,
            pathParams: constructorParams?.pathParams,
            queryParams: constructorParams?.queryParams
        })
    }
}
